import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button, Input, TextArea } from '~/components/ui';
import { priceUnitLabel } from '~/utils';
import { UNIT_PRICE_MAX_DECIMALS } from '~/utils/unitPrice';
import {
  MAX_PRICE,
  MIN_POSITIVE_PRICE,
  parsePriceInput,
  priceMessageParams,
} from '~/utils/moneyBounds';
import { currencyCodeToSymbolMap } from '~/utils/data';
import {
  useOrganizationsContactData,
  organizationContactDataById,
  sellerContactsOf,
} from '~/api';
import { defaultContactIdsByOrg } from '~/hooks/useContactSelection';
import type { Currency, UnitCode } from '~/graphql/graphql';
import {
  SellerContactsSummaryRow,
  type SellerContactOrganization,
} from '~/components/common/SellerContactsSummaryRow';

type NegotiatePriceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizations: SellerContactOrganization[];
  unit: UnitCode;
  currency: Currency;
  isCannedTally?: boolean;
  isSubmitting?: boolean;
  onSubmit: (
    targetPrice: number | null,
    notes: string,
    recipientUserIds: string[],
    organizationIds: string[]
  ) => void;
};

export function NegotiatePriceModal({
  isOpen,
  onClose,
  organizations,
  unit,
  currency,
  isCannedTally = false,
  isSubmitting = false,
  onSubmit,
}: Readonly<NegotiatePriceModalProps>) {
  const { t } = useTranslation('order-builder');
  const [targetPrice, setTargetPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [contactIdsByOrg, setContactIdsByOrg] = useState<Map<string, string[]>>(
    new Map()
  );

  const currencySymbol = currencyCodeToSymbolMap[currency] ?? '';

  const handleSelectionChange = useCallback(
    (organizationId: string, contactIds: string[]) => {
      setContactIdsByOrg((previous) => {
        const next = new Map(previous);
        next.set(organizationId, contactIds);
        return next;
      });
    },
    []
  );

  const organizationIds = useMemo(
    () => organizations.map((organization) => organization.id),
    [organizations]
  );
  const { data: orgContactData, isError: isOrgContactDataError } =
    useOrganizationsContactData(organizationIds);
  const contactDataLoaded =
    orgContactData !== undefined || isOrgContactDataError;
  const contactDataByOrgId = useMemo(
    () => organizationContactDataById(orgContactData?.organizationsByIds),
    [orgContactData]
  );

  const rememberedContactIdsByOrg = useMemo(
    () => defaultContactIdsByOrg(contactDataByOrgId),
    [contactDataByOrgId]
  );
  const selectedContactIdsFor = (organizationId: string) =>
    contactIdsByOrg.get(organizationId) ??
    rememberedContactIdsByOrg.get(organizationId) ??
    [];

  const requestOrganizations = organizations.filter(
    (organization) =>
      !organization.hasPendingNegotiation &&
      !(
        sellerContactsOf(contactDataByOrgId, organization.id).length > 0 &&
        selectedContactIdsFor(organization.id).length === 0
      )
  );
  const recipientUserIds = Array.from(
    new Set(
      requestOrganizations.flatMap((org) => selectedContactIdsFor(org.id))
    )
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (
      isSubmitting ||
      !contactDataLoaded ||
      isOrgContactDataError ||
      requestOrganizations.length === 0
    )
      return;
    const parsedPrice = parsePriceInput(
      targetPrice,
      isCannedTally ? 2 : UNIT_PRICE_MAX_DECIMALS
    );
    if (!parsedPrice.valid) {
      toast.error(
        t(
          isCannedTally
            ? 'order_builder.table.invalid_price'
            : 'order_builder.table.invalid_target_price',
          { ...priceMessageParams, decimals: UNIT_PRICE_MAX_DECIMALS }
        )
      );
      return;
    }
    onSubmit(
      parsedPrice.amount ?? null,
      notes,
      recipientUserIds,
      requestOrganizations.map((organization) => organization.id)
    );
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-neut-light rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] flex flex-col">
        <ModalHeader
          title={t('order_builder.negotiate_price_modal.title')}
          onClose={handleClose}
          closeDisabled={isSubmitting}
        />

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          <form
            id="negotiate-price-form"
            className="space-y-6"
            onSubmit={handleSubmit}
          >
            <Input
              name="targetPrice"
              type="number"
              label={t(
                'order_builder.negotiate_price_modal.target_price_optional'
              )}
              value={targetPrice}
              min={MIN_POSITIVE_PRICE}
              max={MAX_PRICE}
              step="any"
              onChange={(_, value) => setTargetPrice(value)}
              leftIcon={
                <span className="text-xs text-neut-500">{currencySymbol}</span>
              }
              rightIcon={
                <span className="text-xs text-neut-500">
                  /{priceUnitLabel(unit)}
                </span>
              }
            />

            <TextArea
              name="notes"
              label={t('order_builder.negotiate_price_modal.notes_label')}
              placeholder={t(
                'order_builder.negotiate_price_modal.notes_placeholder'
              )}
              value={notes}
              onChange={(_, value) => setNotes(value)}
              rows={4}
            />
          </form>

          <div>
            <h3 className="text-sm font-medium text-neut-700 mb-1">
              {t('order_builder.negotiate_price_modal.recipients_heading')}
            </h3>
            {isOrgContactDataError ? (
              <p className="text-sm text-error-600 px-1 py-2">
                {t('order_builder.negotiate_price_modal.contacts_error')}
              </p>
            ) : organizations.length === 0 ? (
              <p className="text-sm text-neut-sec px-1 py-2">
                {t('order_builder.negotiate_price_modal.no_sellers')}
              </p>
            ) : (
              <ul className="border border-neut-border rounded-lg">
                {organizations.map((organization) => (
                  <SellerContactsSummaryRow
                    key={organization.id}
                    organization={organization}
                    contacts={sellerContactsOf(
                      contactDataByOrgId,
                      organization.id
                    )}
                    loaded={contactDataLoaded}
                    selectedContactIds={selectedContactIdsFor(organization.id)}
                    primaryContactMode="off"
                    onChange={handleSelectionChange}
                    labels={{
                      noContacts: t(
                        'order_builder.negotiate_price_modal.no_contacts'
                      ),
                      noContactsSelected: t(
                        'order_builder.negotiate_price_modal.no_contacts_selected'
                      ),
                    }}
                    testId={`negotiate-seller-${organization.id}`}
                    disabled={isSubmitting}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-neut-border flex items-center gap-3 shrink-0">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('order_builder.negotiate_price_modal.cancel')}
          </Button>
          <Button
            type="submit"
            form="negotiate-price-form"
            variant="primary"
            loading={isSubmitting}
            disabled={
              requestOrganizations.length === 0 ||
              isSubmitting ||
              !contactDataLoaded ||
              isOrgContactDataError
            }
          >
            {t('order_builder.negotiate_price_modal.send')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
